/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains a hook that proxies a hook from 
 * online-patient-management-reducers making less types required to use the hook.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

import { makeStyles, Theme } from '@material-ui/core/styles';

import Grid, { GridProps } from '@material-ui/core/Grid';

import { useField } from 'react-final-form';
/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */





import FieldContext from '../../context/FieldContext';

import OpmsFieldLabel from './OpmsFieldLabel';

import OpmsFieldErrors from './OpmsFieldErrors';

import FieldErrorContext from '../../context/FieldErrorContext';

import LookupsContext from '../../context/LookupsContext';

import ConditionalContext from '../../context/ConditionalContext';
import FieldGroupContext from '../../context/FieldGroupContext';
import LookupContext from '../../context/LookupContext';
import { pascalToCamelCase } from './OpmsInput';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */
interface IOpmsFieldProps extends IOpmsFieldStyleProps {
    name: string;
    label?: React.ReactNode;
    before?: React.ReactNode;
    after?: React.ReactNode;
    instruction?: React.ReactNode;
    xs?: GridProps['xs'];
    sm?: GridProps['sm'];
    md?: GridProps['md'];
    lg?: GridProps['lg'];
    xl?: GridProps['xl'];
}

interface IOpmsFieldStyleProps {
    color?: 'Auto' | 'Inherit' | 'None';
    paddingTop?: boolean | number;
    paddingBottom?: boolean | number;
    isGrouped?: boolean;
}


/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles<Theme, IOpmsFieldStyleProps>(theme => ({
    container: {
        borderBottom: props => props.color === 'Auto' || !props.color ? `1px solid ${theme.palette.grey[300]}` : undefined,
        borderTop: props => props.color === 'Auto' || !props.color ? `1px solid ${theme.palette.grey[300]}` : undefined,
        marginTop: props => props.color === 'Auto' || !props.color ? -1 : undefined,

        '&:nth-child(even)': {
            background: props => props.color === 'Auto' || !props.color ? theme.palette.grey[100] : undefined
        },
    },
    grid: {
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
        paddingTop: props => props.paddingTop === false ?
            theme.spacing(0) :
            props.paddingTop === true ?
                theme.spacing(3) :
                props.paddingTop === undefined && props.isGrouped ?
                    theme.spacing(1) :
                    props.paddingTop === undefined ?
                        theme.spacing(3) :
                        theme.spacing(props.paddingTop),
        paddingBottom: props => props.paddingBottom === false ?
            theme.spacing(0) :
            props.paddingBottom === true ?
                theme.spacing(3) :
                props.paddingBottom === undefined && props.isGrouped ?
                    theme.spacing(1) :
                    props.paddingBottom === undefined ?
                        theme.spacing(3) :
                        theme.spacing(props.paddingBottom)
    }
}));


/*
 * ---------------------------------------------------------------------------------
 * components
 * ---------------------------------------------------------------------------------
 */

const OpmsField: React.FunctionComponent<IOpmsFieldProps> = ({
    name,
    label,
    before,
    after,
    instruction,
    color,
    paddingBottom,
    paddingTop,
    children,
    xl,
    lg,
    md,
    sm,
    xs
}) => {

    const isGrouped = React.useContext(FieldGroupContext);

    const classes = useStyles({
        color: isGrouped ? 'Inherit' : color,
        paddingTop: isGrouped && paddingTop === undefined ? 1 : paddingTop,
        paddingBottom: isGrouped && paddingBottom === undefined ? 1 : paddingBottom,
        isGrouped
    });

    const [errorsExpanded, setErrorsExpanded] = React.useState(true);

    const fieldErrorState = React.useMemo(() => {
        return {
            expanded: errorsExpanded,
            setExpanded: setErrorsExpanded
        }
    }, [errorsExpanded, setErrorsExpanded]);

    const lookups = React.useContext(LookupsContext);

    const lookup = React.useMemo(() => {
        return lookups?.find(l => l.propertyName === name) ?? null;
    }, [name, lookups])

    const conditionalResult = React.useContext(ConditionalContext)

    const fieldName = React.useMemo(() => pascalToCamelCase(name), [name]);

    const { input: { onChange } } = useField(fieldName, {});

    React.useEffect(() => {
        if (conditionalResult?.result === false) {
            onChange(undefined);
        }
    }, [onChange, conditionalResult?.result])

    if (conditionalResult?.mode === 'Show' && conditionalResult?.result === false) {
        return null as any;
    }

    return (
        <div
            className={classes.container}
        >
            {before}
            <LookupContext.Provider value={lookup}>
                <FieldErrorContext.Provider value={fieldErrorState}>
                    <FieldContext.Provider value={name}>
                        <Grid
                            container
                            className={classes.grid}
                        >
                            <Grid
                                item
                                xs={(xs ? xs === 'auto' || xs === 12 ? 'auto' : 12 - (xs as number) : 12) as any}
                                sm={(sm ? sm === 'auto' || sm === 12 ? 'auto' : 12 - (sm as number) : 12) as any}
                                md={(md ? md === 'auto' || md === 12 ? 'auto' : 12 - (md as number) : 8) as any}
                                lg={(lg ? lg === 'auto' || lg === 12 ? 'auto' : 12 - (lg as number) : 9) as any}
                                xl={(xl ? xl === 'auto' || xl === 12 ? 'auto' : 12 - (xl as number) : 9) as any}
                            >
                                {
                                    label !== undefined ?
                                        label :
                                        <OpmsFieldLabel />
                                }

                                {instruction}
                            </Grid>
                            <Grid
                                item
                                xs={xs ?? 12}
                                sm={sm ?? 12}
                                md={md ?? 4}
                                lg={lg ?? 3}
                                xl={xl ?? 3}
                            >
                                {children}
                            </Grid>
                        </Grid>
                        <OpmsFieldErrors />
                    </FieldContext.Provider>
                </FieldErrorContext.Provider>
            </LookupContext.Provider>
            {after}
        </div>
    )
}

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default OpmsField;
